import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Stethoscope } from 'lucide-react';

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 12) return "Good Morning. Ready to prioritize your health today?";
  if (hour >= 12 && hour < 17) return "Good Afternoon. Let's talk about your wellness goals.";
  if (hour >= 17 && hour < 21) return "Good Evening. Our team is available for consultations.";
  return "Our team will respond to your message within 24 hours.";
};

const HeroSection = () => {
  const [greeting, setGreeting] = useState(getGreeting());

  useEffect(() => {
    // Refresh greeting every 5 minutes
    const id = setInterval(() => setGreeting(getGreeting()), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-950 pt-28 sm:pt-32"
      data-testid="hero-section"
    >
      {/* Background Effects */}
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-orange-600/10 to-transparent rounded-full"></div>
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" aria-hidden="true"></div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10 text-center py-12 sm:py-20">
        {/* Dynamic Greeting */}
        <p
          className="text-sm sm:text-base text-blue-300 font-medium mb-6 inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full"
          data-testid="time-based-greeting"
        >
          <Stethoscope className="w-4 h-4" aria-hidden="true" />
          {greeting}
        </p>

        {/* Brand Name */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight" data-testid="hero-brand-name">
            <span className="bg-gradient-to-r from-blue-400 via-orange-400 to-emerald-400 bg-clip-text text-transparent">
              TCUBE FITNESS
            </span>
            <span className="text-blue-400 text-3xl sm:text-4xl md:text-4xl lg:text-5xl align-top relative -top-3 sm:-top-4 md:-top-5 lg:-top-6 ml-1.5 font-normal">™</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl font-semibold mt-3 sm:mt-4 tracking-wide" data-testid="hero-brand-tagline">
            <span className="text-blue-400">Treat</span>
            <span className="text-slate-500 mx-2 sm:mx-3">•</span>
            <span className="text-orange-400">Train</span>
            <span className="text-slate-500 mx-2 sm:mx-3">•</span>
            <span className="text-emerald-400">Transform</span>
          </p>
        </div>

        {/* Headline */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 sm:mb-6 leading-tight max-w-5xl mx-auto" data-testid="hero-headline">
          Your Body Deserves <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Medical Guidance</span>, Not Just Another Diet Plan
        </h1>

        {/* Subheadline */}
        <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed px-4" data-testid="hero-subheadline">
          Personalized nutrition, fitness and lifestyle guidance informed by your health history, goals, lifestyle and medical context, led by a General Medicine (Internal Medicine) physician. For weight management, diabetes, metabolic and women's health. Fully online.
        </p>

        {/* Primary CTA */}
        <div className="flex flex-col items-center gap-4 mb-12 px-4">
          <Link
            to="/book-consultation?from=hero-cta"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white px-8 py-4 rounded-full font-bold text-base sm:text-lg transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-105"
            data-testid="hero-cta-btn"
          >
            Book Your Free Consultation
            <ArrowRight className="w-5 h-5" aria-hidden="true" />
          </Link>

          {/* Secondary Quiz Link */}
          <Link
            to="/find-my-program"
            className="inline-flex items-center gap-1.5 text-slate-300 hover:text-white text-sm sm:text-base underline underline-offset-4 decoration-slate-600 hover:decoration-white transition-colors"
            data-testid="hero-quiz-link"
          >
            Not sure which program suits you? Take our 2-minute quiz
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
